/**
 * /quotes/settings: how the company prices and presents its quotes
 * (docs/10-quote-engine.md §3.2–3.6).
 *
 *   Pricing       pricing models: install cost lines (fixed, per kW, per radiator)
 *                 and the markup or margin on equipment and on installation;
 *                 sizing, VAT and rounding
 *   Packages      the options a quote offers (Good / Better / Best): heat pump
 *                 range, cylinders, fixed items and pricing model
 *   Extras        bolt-ons, included or offered as optional on each quote
 *   Running costs fuel prices, electricity tariffs (time-of-use bands), efficiency
 *   Finance       finance example and deposit shown on the quote
 *   Proposal      wording, what's included, terms, brand colours and logo, the email
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Plus, Save, Trash2, Upload } from "lucide-react";
import { useQuotesApi, type Extra, type Fuel, type Package, type PricingModel, type Product, type QuoteSettings, type Tariff } from "../../quotes/api";
import { gbp } from "../../quotes/format";
import { QuoteTermsCard } from "../../quotes/Access";
import "../../quotes/quotes.css";

type Tab = "pricing" | "packages" | "extras" | "running" | "finance" | "proposal";
const TABS: [Tab, string][] = [
  ["pricing", "Pricing & margins"],
  ["packages", "Packages"],
  ["extras", "Extras"],
  ["running", "Running costs"],
  ["finance", "Finance & deposit"],
  ["proposal", "Proposal & email"],
];

const newId = (p: string) => `${p}-${Math.random().toString(36).slice(2, 10)}`;

export function QuoteSettingsPage() {
  const api = useQuotesApi();
  const [saved, setSaved] = useState<QuoteSettings | null>(null);
  const [draft, setDraft] = useState<QuoteSettings | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [tab, setTab] = useState<Tab>("pricing");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  useEffect(() => {
    Promise.all([api.getSettings(), api.listProducts()])
      .then(([s, ps]) => {
        setSaved(s.settings);
        setDraft(s.settings);
        setProducts(ps);
      })
      .catch((e: Error) => setMessage({ tone: "danger", text: e.message }));
  }, [api]);

  const dirty = saved !== null && JSON.stringify(saved) !== JSON.stringify(draft);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function save() {
    if (!draft) return;
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.saveSettings(draft);
      setSaved(r.settings);
      setDraft(r.settings);
      setMessage({ tone: "success", text: "Settings saved. New and recalculated quotes use them." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (!draft) return <div className="page">{message ? <div className={`flash flash-${message.tone}`}>{message.text}</div> : <p className="muted">Loading…</p>}</div>;
  const set = (patch: Partial<QuoteSettings>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  return (
    <div className="page q-page q-settings">
      <div className="page-head q-head">
        <div>
          <h1>Quote settings</h1>
          <p>Your costs, margins, the options you offer and how your quotes look. Sent quotes keep the prices they were sent with.</p>
        </div>
        <div className="q-head-actions">
          <button type="button" className="btn btn-secondary" disabled={!dirty || saving} onClick={() => setDraft(saved)}>
            Discard
          </button>
          <button type="button" className="btn btn-primary" disabled={!dirty || saving} onClick={() => void save()}>
            <Save size={16} aria-hidden="true" /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}

      <QuoteTermsCard compact />

      <div className="ed-segmented q-tabs" role="tablist">
        {TABS.map(([t, l]) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? "is-on" : ""} onClick={() => setTab(t)}>
            {l}
          </button>
        ))}
      </div>

      {tab === "pricing" && <PricingTab s={draft} set={set} />}
      {tab === "packages" && <PackagesTab s={draft} set={set} products={products} />}
      {tab === "extras" && <ExtrasTab s={draft} set={set} />}
      {tab === "running" && <RunningTab s={draft} set={set} />}
      {tab === "finance" && <FinanceTab s={draft} set={set} />}
      {tab === "proposal" && <ProposalTab s={draft} set={set} uploadLogo={api.uploadLogo} />}
    </div>
  );
}

type TabProps = { s: QuoteSettings; set: (p: Partial<QuoteSettings>) => void };

function Num({ id, label, value, onChange, unit, step = 1, min = 0, max, hint }: { id: string; label: string; value: number | null | undefined; onChange: (v: number) => void; unit?: string; step?: number; min?: number; max?: number; hint?: string }) {
  return (
    <div className="ed-field">
      <label className="ed-label" htmlFor={id}>
        {label}
      </label>
      <div className="q-unit-input">
        <input id={id} className="input" type="number" step={step} min={min} max={max} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} />
        {unit && <span>{unit}</span>}
      </div>
      {hint && <p className="ed-hint">{hint}</p>}
    </div>
  );
}

function Txt({ id, label, value, onChange, hint, rows, placeholder }: { id: string; label: string; value: string; onChange: (v: string) => void; hint?: string; rows?: number; placeholder?: string }) {
  return (
    <div className="ed-field">
      <label className="ed-label" htmlFor={id}>
        {label}
      </label>
      {rows ? (
        <textarea id={id} className="input" rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input id={id} className="input" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
      {hint && <p className="ed-hint">{hint}</p>}
    </div>
  );
}

function Switch({ id, checked, onChange, children }: { id: string; checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="ed-toggle q-toggle-row" htmlFor={id}>
      <input id={id} type="checkbox" role="switch" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="ed-toggle-track" aria-hidden="true" />
      {children}
    </label>
  );
}

/** A list of text lines edited as a textarea (one per line). */
function Lines({ id, label, value, onChange, hint }: { id: string; label: string; value: string[]; onChange: (v: string[]) => void; hint?: string }) {
  const [text, setText] = useState(value.join("\n"));
  return (
    <Txt
      id={id}
      label={label}
      rows={Math.max(3, value.length + 1)}
      value={text}
      hint={hint ?? "One per line."}
      onChange={(t) => {
        setText(t);
        onChange(t.split("\n").map((x) => x.trim()).filter(Boolean));
      }}
    />
  );
}

// ─── Pricing & margins ───────────────────────────────────────────────────────

function PricingTab({ s, set }: TabProps) {
  const setModel = (i: number, patch: Partial<PricingModel>) => set({ pricingModels: s.pricingModels.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  return (
    <>
      {s.pricingModels.map((m, i) => {
        const install = m.lines.reduce((t, l) => t + (l.basis === "fixed" ? l.cost : 0), 0);
        const example = (cost: number, pctv: number) => (m.mode === "margin" ? cost / (1 - Math.min(pctv, 90) / 100) : cost * (1 + pctv / 100));
        return (
          <section key={m.id} className="card">
            <div className="q-card-head">
              <h2>{m.name || "Pricing model"}</h2>
              {s.pricingModels.length > 1 && (
                <button type="button" className="link-button small q-danger" onClick={() => set({ pricingModels: s.pricingModels.filter((_, j) => j !== i) })}>
                  <Trash2 size={14} aria-hidden="true" /> Remove
                </button>
              )}
            </div>
            <p className="card-sub">Your costs for an installation and the margin you add. Packages use a pricing model; each quote can override the markup.</p>
            <div className="q-row-fields">
              <Txt id={`pm-name-${i}`} label="Name" value={m.name} onChange={(v) => setModel(i, { name: v })} />
              <div className="ed-field">
                <label className="ed-label">How you add margin</label>
                <div className="ed-segmented" role="radiogroup">
                  {(
                    [
                      ["markup", "Markup on cost"],
                      ["margin", "Margin of price"],
                    ] as ["markup" | "margin", string][]
                  ).map(([v, l]) => (
                    <button key={v} type="button" role="radio" aria-checked={m.mode === v} className={m.mode === v ? "is-on" : ""} onClick={() => setModel(i, { mode: v })}>
                      {l}
                    </button>
                  ))}
                </div>
                <p className="ed-hint">{m.mode === "markup" ? "Price = cost × (1 + markup)." : "Price = cost ÷ (1 − margin): the margin is your share of the price."}</p>
              </div>
              <Num
                id={`pm-hw-${i}`}
                label={m.mode === "markup" ? "Markup on equipment" : "Margin on equipment"}
                unit="%"
                max={m.mode === "margin" ? 90 : 500}
                value={m.hardwareMarkupPercent}
                onChange={(v) => setModel(i, { hardwareMarkupPercent: v })}
                hint={`£1,000 of equipment sells for ${gbp(Math.round(example(1000, m.hardwareMarkupPercent)))}`}
              />
              <Num
                id={`pm-in-${i}`}
                label={m.mode === "markup" ? "Markup on installation" : "Margin on installation"}
                unit="%"
                max={m.mode === "margin" ? 90 : 500}
                value={m.installMarkupPercent}
                onChange={(v) => setModel(i, { installMarkupPercent: v })}
                hint={`Your fixed install costs of ${gbp(install)} sell for ${gbp(Math.round(example(install, m.installMarkupPercent)))}`}
              />
            </div>
            <h3>Installation cost lines</h3>
            <div className="table-scroll">
              <table className="stats-table q-edit-table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Charged</th>
                    <th>Type</th>
                    <th className="num">Your cost</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {m.lines.map((l, k) => {
                    const upd = (patch: Partial<typeof l>) => setModel(i, { lines: m.lines.map((x, j) => (j === k ? { ...x, ...patch } : x)) });
                    return (
                      <tr key={l.id}>
                        <td>
                          <input className="input" aria-label="Line name" value={l.label} onChange={(e) => upd({ label: e.target.value })} />
                        </td>
                        <td>
                          <select className="input" aria-label="Charged" value={l.basis} onChange={(e) => upd({ basis: e.target.value as typeof l.basis })}>
                            <option value="fixed">Per install</option>
                            <option value="per_kw">Per kW of heat pump</option>
                            <option value="per_radiator">Per radiator</option>
                          </select>
                        </td>
                        <td>
                          <select className="input" aria-label="Type" value={l.group} onChange={(e) => upd({ group: e.target.value as typeof l.group })}>
                            <option value="labour">Labour</option>
                            <option value="materials">Materials</option>
                            <option value="other">Other</option>
                          </select>
                        </td>
                        <td>
                          <input className="input q-money" type="number" min={0} aria-label="Cost" value={l.cost} onChange={(e) => upd({ cost: Number(e.target.value) || 0 })} />
                        </td>
                        <td>
                          <button type="button" className="link-button small" aria-label="Remove line" onClick={() => setModel(i, { lines: m.lines.filter((_, j) => j !== k) })}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button type="button" className="btn btn-secondary" onClick={() => setModel(i, { lines: [...m.lines, { id: newId("ln"), label: "", basis: "fixed", cost: 0, group: "labour" }] })}>
              <Plus size={16} aria-hidden="true" /> Add a cost line
            </button>
          </section>
        );
      })}
      <button
        type="button"
        className="btn btn-secondary q-add-card"
        onClick={() => set({ pricingModels: [...s.pricingModels, { ...s.pricingModels[0], id: newId("pm"), name: `${s.pricingModels[0]?.name ?? "Pricing"} (copy)`, lines: s.pricingModels[0]?.lines.map((l) => ({ ...l, id: newId("ln") })) ?? [] }] })}
      >
        <Plus size={16} aria-hidden="true" /> Add a pricing model
      </button>

      <section className="card">
        <h2>Sizing, VAT and rounding</h2>
        <div className="q-row-fields">
          <Num id="sz-margin" label="Sizing margin over the heat loss" unit="%" max={50} value={s.sizing.marginPercent} onChange={(v) => set({ sizing: { ...s.sizing, marginPercent: v } })} hint="MCS sizes to the design heat loss: 0% unless you have a reason." />
          <Num id="sz-lpp" label="Hot water per person" unit="litres" min={20} max={100} value={s.sizing.litresPerPerson} onChange={(v) => set({ sizing: { ...s.sizing, litresPerPerson: v } })} hint="Cylinder size = people × this. MIS 3005-D uses 45 litres a day per person." />
          <Num id="sz-min" label="Smallest cylinder" unit="litres" max={500} value={s.sizing.minCylinderL} onChange={(v) => set({ sizing: { ...s.sizing, minCylinderL: v } })} />
          <Num id="sz-flow" label="Default design flow temperature" unit="°C" min={30} max={70} value={s.sizing.defaultFlowTempC} onChange={(v) => set({ sizing: { ...s.sizing, defaultFlowTempC: v } })} />
          <Num id="vat" label="VAT on installations" unit="%" max={20} value={s.vat.installRate} onChange={(v) => set({ vat: { installRate: v } })} hint="0% for heat pump installations in Great Britain until 31 March 2027 (5% after, unless extended)." />
          <div className="ed-field">
            <label className="ed-label" htmlFor="rounding">
              Round prices to
            </label>
            <select id="rounding" className="input" value={s.rounding} onChange={(e) => set({ rounding: Number(e.target.value) })}>
              {[0, 1, 10, 50, 100].map((r) => (
                <option key={r} value={r}>
                  {r === 0 ? "No rounding" : `Nearest £${r}`}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Switch id="sz-cyl" checked={s.sizing.includeCylinder} onChange={(v) => set({ sizing: { ...s.sizing, includeCylinder: v } })}>
          Include a hot water cylinder in every option
        </Switch>
      </section>
    </>
  );
}

// ─── Packages ────────────────────────────────────────────────────────────────

function PackagesTab({ s, set, products }: TabProps & { products: Product[] }) {
  const pumps = useMemo(() => products.filter((p) => p.category === "heat_pump"), [products]);
  const cyls = useMemo(() => products.filter((p) => p.category === "cylinder"), [products]);
  const others = useMemo(() => products.filter((p) => p.category !== "heat_pump" && p.category !== "cylinder"), [products]);
  const byId = useMemo(() => new Map(products.map((p) => [p.productId, p])), [products]);
  const setPkg = (i: number, patch: Partial<Package>) => set({ packages: s.packages.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const move = (i: number, d: number) => {
    const list = [...s.packages];
    const [p] = list.splice(i, 1);
    list.splice(i + d, 0, p);
    set({ packages: list });
  };

  if (!products.length) {
    return (
      <div className="card empty">
        <h2>Add products first</h2>
        <p>Packages are built from your catalogue.</p>
        <Link to="/quotes/catalogue" className="btn btn-primary">
          Go to the catalogue
        </Link>
      </div>
    );
  }

  return (
    <>
      <p className="muted">Each package is one option on a quote. For each home we pick the smallest heat pump in the package that covers its heat loss, and the smallest cylinder big enough for the household.</p>
      {s.packages.map((pkg, i) => (
        <section key={pkg.id} className={`card${pkg.active ? "" : " is-off"}`}>
          <div className="q-card-head">
            <h2>
              {pkg.tier && <span className="q-tier-chip">{pkg.tier}</span>} {pkg.name || "New package"}
            </h2>
            <div className="q-inline">
              <Switch id={`pk-on-${i}`} checked={pkg.active} onChange={(v) => setPkg(i, { active: v })}>
                Offered
              </Switch>
              <button type="button" className="link-button small" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                <ArrowUp size={14} />
              </button>
              <button type="button" className="link-button small" aria-label="Move down" disabled={i === s.packages.length - 1} onClick={() => move(i, 1)}>
                <ArrowDown size={14} />
              </button>
              <button type="button" className="link-button small q-danger" onClick={() => set({ packages: s.packages.filter((_, j) => j !== i) })}>
                <Trash2 size={14} aria-hidden="true" /> Remove
              </button>
            </div>
          </div>
          <div className="q-row-fields">
            <Txt id={`pk-name-${i}`} label="Name" value={pkg.name} onChange={(v) => setPkg(i, { name: v })} />
            <Txt id={`pk-tier-${i}`} label="Label (e.g. Good, Better, Best)" value={pkg.tier} onChange={(v) => setPkg(i, { tier: v })} />
            <div className="ed-field">
              <label className="ed-label" htmlFor={`pk-model-${i}`}>
                Pricing model
              </label>
              <select id={`pk-model-${i}`} className="input" value={pkg.pricingModelId ?? ""} onChange={(e) => setPkg(i, { pricingModelId: e.target.value })}>
                {s.pricingModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Txt id={`pk-desc-${i}`} label="Description (shown to the customer)" rows={2} value={pkg.description} onChange={(v) => setPkg(i, { description: v })} />
          <Lines id={`pk-hl-${i}`} label="Highlights (shown as ticks)" value={pkg.highlights} onChange={(v) => setPkg(i, { highlights: v })} />
          <div className="q-pick-grid">
            <ProductPicker label="Heat pumps in this package" options={pumps} value={pkg.heatPumpIds} onChange={(v) => setPkg(i, { heatPumpIds: v })} describe={(p) => `${p.kw} kW${p.designOutputKw && p.designOutputKw !== p.kw ? ` (${p.designOutputKw} at design)` : ""} · ${gbp(p.costExVat)}`} />
            <ProductPicker label="Cylinders" options={cyls} value={pkg.cylinderIds} onChange={(v) => setPkg(i, { cylinderIds: v })} describe={(p) => `${p.litres} L · ${gbp(p.costExVat)}`} />
          </div>
          <h3>Always included</h3>
          <div className="table-scroll">
            <table className="stats-table q-edit-table">
              <tbody>
                {pkg.items.map((it, k) => {
                  const p = byId.get(it.productId);
                  return (
                    <tr key={`${it.productId}-${k}`}>
                      <td>{p ? p.name : <span className="muted">Removed from the catalogue</span>}</td>
                      <td className="num muted">{p ? gbp(p.costExVat) : ""}</td>
                      <td>
                        <input className="input q-qty" type="number" min={1} max={99} aria-label="Quantity" value={it.qty} onChange={(e) => setPkg(i, { items: pkg.items.map((x, j) => (j === k ? { ...x, qty: Number(e.target.value) || 1 } : x)) })} />
                      </td>
                      <td>
                        <button type="button" className="link-button small" aria-label="Remove item" onClick={() => setPkg(i, { items: pkg.items.filter((_, j) => j !== k) })}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <select className="input q-add-select" aria-label="Add an item" value="" onChange={(e) => e.target.value && setPkg(i, { items: [...pkg.items, { productId: e.target.value, qty: 1 }] })}>
            <option value="">Add an item from the catalogue…</option>
            {others.map((p) => (
              <option key={p.productId} value={p.productId}>
                {p.name} ({gbp(p.costExVat)})
              </option>
            ))}
          </select>
        </section>
      ))}
      <button
        type="button"
        className="btn btn-secondary q-add-card"
        onClick={() => set({ packages: [...s.packages, { id: newId("pk"), name: "", tier: "", description: "", highlights: [], heatPumpIds: [], cylinderIds: [], items: [], pricingModelId: s.pricingModels[0]?.id ?? null, active: true }] })}
      >
        <Plus size={16} aria-hidden="true" /> Add a package
      </button>
    </>
  );
}

/** Choose products from a list, grouped by brand, with a filter. */
function ProductPicker({ label, options, value, onChange, describe }: { label: string; options: Product[]; value: string[]; onChange: (v: string[]) => void; describe: (p: Product) => string }) {
  const [q, setQ] = useState("");
  const shown = options.filter((p) => !q || `${p.name} ${p.brand}`.toLowerCase().includes(q.toLowerCase()));
  const chosen = new Set(value);
  return (
    <div className="q-picker">
      <div className="q-picker-head">
        <span className="ed-label">
          {label} ({value.length})
        </span>
        <input className="input q-picker-filter" placeholder="Filter" value={q} onChange={(e) => setQ(e.target.value)} aria-label={`Filter ${label}`} />
      </div>
      <div className="q-picker-list">
        {shown.map((p) => (
          <label key={p.productId} className={`q-picker-item${chosen.has(p.productId) ? " is-on" : ""}${p.active ? "" : " is-inactive"}`}>
            <input type="checkbox" checked={chosen.has(p.productId)} onChange={(e) => onChange(e.target.checked ? [...value, p.productId] : value.filter((x) => x !== p.productId))} />
            <span>
              {p.name}
              <small>{describe(p)}</small>
            </span>
          </label>
        ))}
        {shown.length === 0 && <p className="muted">Nothing matches.</p>}
      </div>
    </div>
  );
}

// ─── Extras ──────────────────────────────────────────────────────────────────

function ExtrasTab({ s, set }: TabProps) {
  const setExtra = (i: number, patch: Partial<Extra>) => set({ extras: s.extras.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  return (
    <section className="card">
      <h2>Extras</h2>
      <p className="card-sub">Bolt-ons with their own sell price and VAT rate. “Included” puts them in every option's price; “Optional” lets the customer add them on their quote page. You can change this per quote.</p>
      <div className="table-scroll">
        <table className="stats-table q-edit-table q-extras-edit">
          <thead>
            <tr>
              <th>Extra</th>
              <th>Charged</th>
              <th className="num">Your cost</th>
              <th className="num">Price ex VAT</th>
              <th className="num">VAT %</th>
              <th>Default</th>
              <th className="num">Max qty</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {s.extras.map((e, i) => (
              <tr key={e.id}>
                <td>
                  <input className="input" aria-label="Name" value={e.name} onChange={(ev) => setExtra(i, { name: ev.target.value })} />
                  <input className="input q-desc-input" aria-label="Description" placeholder="Description for the customer" value={e.description} onChange={(ev) => setExtra(i, { description: ev.target.value })} />
                </td>
                <td>
                  <select className="input" aria-label="Charged" value={e.basis} onChange={(ev) => setExtra(i, { basis: ev.target.value as Extra["basis"] })}>
                    <option value="fixed">Once</option>
                    <option value="per_unit">Per unit</option>
                  </select>
                  {e.basis === "per_unit" && <input className="input q-desc-input" aria-label="Unit" placeholder="unit, e.g. radiator" value={e.unitLabel} onChange={(ev) => setExtra(i, { unitLabel: ev.target.value })} />}
                </td>
                <td>
                  <input className="input q-money" type="number" min={0} aria-label="Cost" value={e.unitCost} onChange={(ev) => setExtra(i, { unitCost: Number(ev.target.value) || 0 })} />
                </td>
                <td>
                  <input className="input q-money" type="number" min={0} aria-label="Price" value={e.unitPrice} onChange={(ev) => setExtra(i, { unitPrice: Number(ev.target.value) || 0 })} />
                </td>
                <td>
                  <select className="input q-qty" aria-label="VAT rate" value={e.vatRate} onChange={(ev) => setExtra(i, { vatRate: Number(ev.target.value) })}>
                    <option value={0}>0</option>
                    <option value={5}>5</option>
                    <option value={20}>20</option>
                  </select>
                </td>
                <td>
                  <select className="input" aria-label="Default" value={e.mode} onChange={(ev) => setExtra(i, { mode: ev.target.value as Extra["mode"] })}>
                    <option value="included">Included</option>
                    <option value="optional">Optional</option>
                    <option value="off">Off</option>
                  </select>
                </td>
                <td>
                  <input className="input q-qty" type="number" min={1} max={99} aria-label="Max quantity" value={e.maxQty} disabled={e.basis !== "per_unit"} onChange={(ev) => setExtra(i, { maxQty: Number(ev.target.value) || 1 })} />
                </td>
                <td>
                  <button type="button" className="link-button small" aria-label="Remove extra" onClick={() => set({ extras: s.extras.filter((_, j) => j !== i) })}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => set({ extras: [...s.extras, { id: newId("ex"), name: "", description: "", basis: "fixed", unitLabel: "", unitCost: 0, unitPrice: 0, vatRate: 0, mode: "optional", defaultQty: 1, maxQty: 1, qtyFrom: null }] })}
      >
        <Plus size={16} aria-hidden="true" /> Add an extra
      </button>
      <p className="ed-hint">VAT: energy-saving work that's part of the heat pump installation is 0% until 31 March 2027; separate items such as an EV charger or a smart thermostat bought on its own are usually 20%.</p>
    </section>
  );
}

// ─── Running costs ───────────────────────────────────────────────────────────

const FUEL_LABELS: Record<Fuel, string> = { gas: "Mains gas", oil: "Heating oil", lpg: "LPG", electric: "Electric heating", other: "Other fuels" };

function RunningTab({ s, set }: TabProps) {
  const rc = s.runningCosts;
  const setRc = (patch: Partial<QuoteSettings["runningCosts"]>) => set({ runningCosts: { ...rc, ...patch } });
  const setTariff = (i: number, patch: Partial<Tariff>) => setRc({ tariffs: rc.tariffs.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  return (
    <>
      <section className="card">
        <h2>Running costs on quotes</h2>
        <Switch id="rc-on" checked={rc.enabled} onChange={(v) => setRc({ enabled: v })}>
          Show running costs and savings
        </Switch>
        <div className="q-row-fields">
          <div className="ed-field">
            <label className="ed-label">Heat pump efficiency from</label>
            <div className="ed-segmented" role="radiogroup">
              {(
                [
                  ["product", "Product SCOP"],
                  ["mcs031", "MCS 031 table"],
                ] as ["product" | "mcs031", string][]
              ).map(([v, l]) => (
                <button key={v} type="button" role="radio" aria-checked={rc.efficiencySource === v} className={rc.efficiencySource === v ? "is-on" : ""} onClick={() => setRc({ efficiencySource: v })}>
                  {l}
                </button>
              ))}
            </div>
            <p className="ed-hint">
              {rc.efficiencySource === "product"
                ? "Each heat pump's SCOP at the design flow temperature (from its 35 °C and 55 °C figures), so options differ."
                : "MCS 031's seasonal performance by flow temperature: the same for every air source heat pump."}
            </p>
          </div>
          <Num id="rc-dhw" label="Hot water efficiency" step={0.1} min={1} max={6} value={rc.hotWaterScop} onChange={(v) => setRc({ hotWaterScop: v })} hint="MCS 031 uses 1.9 for air source heat pumps." />
          <Num id="rc-dd" label="Degree days (fallback)" min={500} max={5000} value={rc.degreeDays} onChange={(v) => setRc({ degreeDays: v })} hint="Used only when the postcode area isn't in MCS 031 Table 1." />
          <Num id="rc-lpd" label="Hot water use" unit="litres / person / day" min={10} max={200} value={rc.hotWaterLitresPerPersonDay} onChange={(v) => setRc({ hotWaterLitresPerPersonDay: v })} />
        </div>
        <Switch id="rc-epc" checked={rc.useEpcDemand} onChange={(v) => setRc({ useEpcDemand: v })}>
          Use the EPC's own heating and hot water demand when the certificate has it (2024+ certificates)
        </Switch>
        <Switch id="rc-gas" checked={rc.gasDisconnected} onChange={(v) => setRc({ gasDisconnected: v })}>
          Count the gas standing charge as saved (the customer disconnects gas)
        </Switch>
        <Txt id="rc-note" label="Note shown under the running costs" rows={2} value={rc.note} onChange={(v) => setRc({ note: v })} />
      </section>

      <section className="card">
        <h2>Today's heating</h2>
        <p className="card-sub">What the customer's current heating costs. Defaults: Ofgem price cap October to December 2026; oil and LPG from published averages; boiler efficiency from SAP 10.2.</p>
        <div className="table-scroll">
          <table className="stats-table q-edit-table">
            <thead>
              <tr>
                <th>Fuel</th>
                <th className="num">Price p/kWh</th>
                <th className="num">Standing p/day</th>
                <th className="num">Boiler efficiency %</th>
              </tr>
            </thead>
            <tbody>
              {(Object.keys(FUEL_LABELS) as Fuel[]).map((f) => (
                <tr key={f}>
                  <td>{FUEL_LABELS[f]}</td>
                  {(["pencePerKwh", "standingPencePerDay", "efficiencyPercent"] as const).map((k) => (
                    <td key={k}>
                      <input className="input q-money" type="number" step="0.01" aria-label={`${FUEL_LABELS[f]} ${k}`} value={rc.fuels[f][k]} onChange={(e) => setRc({ fuels: { ...rc.fuels, [f]: { ...rc.fuels[f], [k]: Number(e.target.value) || 0 } } })} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h2>Electricity tariffs</h2>
        <p className="card-sub">The customer can switch between these on their quote. Each rate applies from its start hour until the next one. The average is weighted by hours.</p>
        {rc.tariffs.map((t, i) => (
          <div key={t.id} className="q-tariff">
            <div className="q-row-fields">
              <Txt id={`tf-name-${i}`} label="Tariff" value={t.name} onChange={(v) => setTariff(i, { name: v })} />
              <Num id={`tf-sc-${i}`} label="Standing charge" unit="p/day" step={0.01} value={t.standingPencePerDay} onChange={(v) => setTariff(i, { standingPencePerDay: v })} />
              <Num id={`tf-share-${i}`} label="Share of use in the cheapest hours" unit="%" max={100} value={t.offPeakSharePercent ?? 0} onChange={(v) => setTariff(i, { offPeakSharePercent: v || null })} hint="Blank or 0: spread evenly over the day." />
              <div className="ed-field">
                <label className="ed-label">Default</label>
                <label className="q-check">
                  <input type="radio" name="default-tariff" checked={rc.defaultTariffId === t.id} onChange={() => setRc({ defaultTariffId: t.id })} /> Show first
                </label>
              </div>
            </div>
            <div className="q-bands">
              {t.bands.map((b, k) => (
                <span key={k} className="q-band">
                  from
                  <select aria-label="Start hour" className="input q-qty" value={b.startHour} onChange={(e) => setTariff(i, { bands: t.bands.map((x, j) => (j === k ? { ...x, startHour: Number(e.target.value) } : x)) })}>
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>
                        {String(h).padStart(2, "0")}:00
                      </option>
                    ))}
                  </select>
                  <input aria-label="Rate" className="input q-money" type="number" step="0.01" value={b.pencePerKwh} onChange={(e) => setTariff(i, { bands: t.bands.map((x, j) => (j === k ? { ...x, pencePerKwh: Number(e.target.value) || 0 } : x)) })} />
                  p
                  {t.bands.length > 1 && (
                    <button type="button" className="link-button small" aria-label="Remove rate" onClick={() => setTariff(i, { bands: t.bands.filter((_, j) => j !== k) })}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </span>
              ))}
              <button type="button" className="link-button small" onClick={() => setTariff(i, { bands: [...t.bands, { startHour: Math.min(23, (t.bands[t.bands.length - 1]?.startHour ?? 0) + 1), pencePerKwh: t.bands[0]?.pencePerKwh ?? 25 }] })}>
                <Plus size={12} aria-hidden="true" /> Rate
              </button>
              <button type="button" className="link-button small q-danger" onClick={() => setRc({ tariffs: rc.tariffs.filter((_, j) => j !== i) })}>
                <Trash2 size={12} aria-hidden="true" /> Remove tariff
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-secondary" onClick={() => setRc({ tariffs: [...rc.tariffs, { id: newId("tf"), name: "New tariff", standingPencePerDay: 50, bands: [{ startHour: 0, pencePerKwh: 25 }], offPeakSharePercent: null, sourceUrl: "" }] })}>
          <Plus size={16} aria-hidden="true" /> Add a tariff
        </button>
      </section>
    </>
  );
}

// ─── Finance & deposit ───────────────────────────────────────────────────────

function FinanceTab({ s, set }: TabProps) {
  const f = s.finance;
  const d = s.deposit;
  const setF = (patch: Partial<QuoteSettings["finance"]>) => set({ finance: { ...f, ...patch } });
  const setD = (patch: Partial<QuoteSettings["deposit"]>) => set({ deposit: { ...d, ...patch } });
  return (
    <>
      <section className="card">
        <h2>Finance</h2>
        <div className="flash flash-info">
          Only switch this on if you're authorised by the FCA to broker credit (or an appointed representative of a firm that is). Quoting a rate means showing a representative example
          (CONC 3.5), which the quote page does for you, and saying you're a credit broker, not a lender.
        </div>
        <Switch id="fin-on" checked={f.enabled} onChange={(v) => setF({ enabled: v })}>
          Show a finance option on quotes
        </Switch>
        <div className="q-row-fields">
          <Txt id="fin-lender" label="Lender" value={f.lender} onChange={(v) => setF({ lender: v })} />
          <Num id="fin-apr" label="Representative APR" unit="%" step={0.1} max={50} value={f.aprPercent} onChange={(v) => setF({ aprPercent: v })} />
          <Txt id="fin-terms" label="Terms (months)" value={f.termsMonths.join(", ")} onChange={(v) => setF({ termsMonths: v.split(/[,\s]+/).map(Number).filter((n) => n >= 6 && n <= 240) })} hint="e.g. 36, 60, 120" />
          <Num id="fin-min" label="Smallest loan" unit="£" value={f.minLoan} onChange={(v) => setF({ minLoan: v })} />
          <Num id="fin-max" label="Largest loan" unit="£" value={f.maxLoan} onChange={(v) => setF({ maxLoan: v })} />
          <Num id="fin-dep" label="Deposit" unit="%" max={90} value={f.depositPercent} onChange={(v) => setF({ depositPercent: v })} />
        </div>
        <Txt id="fin-notice" label="Your credit broker statement" rows={3} value={f.notice} onChange={(v) => setF({ notice: v })} />
      </section>
      <section className="card">
        <h2>Deposit</h2>
        <Switch id="dep-on" checked={d.enabled} onChange={(v) => setD({ enabled: v })}>
          Show a deposit due on acceptance
        </Switch>
        <div className="q-row-fields">
          <div className="ed-field">
            <label className="ed-label" htmlFor="dep-type">
              Deposit
            </label>
            <select id="dep-type" className="input" value={d.type} onChange={(e) => setD({ type: e.target.value as "fixed" | "percent" })}>
              <option value="percent">% of the price</option>
              <option value="fixed">Fixed amount</option>
            </select>
          </div>
          <Num id="dep-val" label={d.type === "percent" ? "Percent" : "Amount"} unit={d.type === "percent" ? "%" : "£"} value={d.value} onChange={(v) => setD({ value: v })} />
          <Txt id="dep-note" label="Note" value={d.note} onChange={(v) => setD({ note: v })} />
        </div>
        <p className="ed-hint">The quote shows the deposit; taking card payments on acceptance (Stripe) is planned next (docs/10 §10).</p>
      </section>
    </>
  );
}

// ─── Proposal & email ────────────────────────────────────────────────────────

function ProposalTab({ s, set, uploadLogo }: TabProps & { uploadLogo: (f: File) => Promise<string> }) {
  const p = s.proposal;
  const setP = (patch: Partial<QuoteSettings["proposal"]>) => set({ proposal: { ...p, ...patch } });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <section className="card">
        <h2>Look</h2>
        <div className="q-row-fields">
          <div className="ed-field">
            <label className="ed-label" htmlFor="br-primary">
              Main colour
            </label>
            <input id="br-primary" type="color" value={p.brand.primary} onChange={(e) => setP({ brand: { ...p.brand, primary: e.target.value.toUpperCase() } })} />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="br-accent">
              Highlight colour
            </label>
            <input id="br-accent" type="color" value={p.brand.accent} onChange={(e) => setP({ brand: { ...p.brand, accent: e.target.value.toUpperCase() } })} />
          </div>
          <div className="ed-field">
            <span className="ed-label">Logo</span>
            <div className="q-inline">
              {p.brand.logoUrl && <img src={p.brand.logoUrl} alt="" className="q-logo-preview" />}
              <label className="btn btn-secondary">
                <Upload size={16} aria-hidden="true" /> {uploading ? "Uploading…" : p.brand.logoUrl ? "Replace" : "Upload"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploading(true);
                    setError(null);
                    uploadLogo(file)
                      .then((url) => setP({ brand: { ...p.brand, logoUrl: url } }))
                      .catch((err: Error) => setError(err.message))
                      .finally(() => setUploading(false));
                  }}
                />
              </label>
              {p.brand.logoUrl && (
                <button type="button" className="link-button small" onClick={() => setP({ brand: { ...p.brand, logoUrl: "" } })}>
                  Remove
                </button>
              )}
            </div>
            {error && <p className="ed-hint q-danger">{error}</p>}
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="pr-display">
              Prices shown to the customer
            </label>
            <select id="pr-display" className="input" value={p.priceDisplay} onChange={(e) => setP({ priceDisplay: e.target.value as typeof p.priceDisplay })}>
              <option value="grouped">Grouped (equipment, installation, extras)</option>
              <option value="itemised">Every line with its price</option>
              <option value="total">What's included, one price</option>
            </select>
          </div>
          <Num id="pr-valid" label="Quotes valid for" unit="days" min={1} max={365} value={p.validityDays} onChange={(v) => setP({ validityDays: v })} />
        </div>
        <Switch id="pr-hl" checked={p.showHeatLoss} onChange={(v) => setP({ showHeatLoss: v })}>
          Show how the heat pump was sized (the heat loss calculation)
        </Switch>
        <Switch id="pr-rc" checked={p.showRunningCosts} onChange={(v) => setP({ showRunningCosts: v })}>
          Show running costs and savings
        </Switch>
      </section>
      <section className="card">
        <h2>Wording</h2>
        <Txt id="pr-intro" label="Introduction" rows={3} value={p.intro} onChange={(v) => setP({ intro: v })} />
        <Lines id="pr-inc" label="Included as standard" value={p.whatsIncluded} onChange={(v) => setP({ whatsIncluded: v })} />
        <Lines id="pr-why" label="Why choose us" value={p.whyChooseUs} onChange={(v) => setP({ whyChooseUs: v })} />
        <Txt id="pr-accept" label="Shown above the Accept button" rows={2} value={p.acceptanceText} onChange={(v) => setP({ acceptanceText: v })} />
        <Txt id="pr-terms" label="Terms and conditions" rows={8} value={p.terms} onChange={(v) => setP({ terms: v })} />
        <Txt id="pr-terms-v" label="Terms version" value={p.termsVersion} onChange={(v) => setP({ termsVersion: v })} hint="Change this when you change the terms: each acceptance records the version it agreed to." />
      </section>
      <section className="card">
        <h2>Email</h2>
        <Txt id="pr-subj" label="Default subject" value={p.emailSubject} placeholder="Your heat pump quote from (your company)" onChange={(v) => setP({ emailSubject: v })} />
        <Txt id="pr-msg" label="Default message" rows={5} value={p.emailMessage} placeholder="Hi (first name), thank you for your interest…" onChange={(v) => setP({ emailMessage: v })} />
        <Lines id="pr-notify" label="Tell these addresses when a customer opens, accepts or declines a quote" value={s.notifyEmails} onChange={(v) => set({ notifyEmails: v })} hint="One per line; each must be confirmed (Website components → Notifications). Blank: the person who sent the quote." />
      </section>
    </>
  );
}
